/**
 * PolicyAcceptanceModal — Full-screen modal shown after login if latest
 * policy not accepted.
 *
 * iter145:
 *   • Content source migrated to Admin Panel API (via backend proxy at
 *     /api/legal/policy). Admin returns HTML — we render it via
 *     DOMPurify-sanitised `dangerouslySetInnerHTML`.
 *   • Local Storage cache is version-scoped and used as a read-only
 *     fallback when the network is unavailable (offline compliance
 *     surface). Cache is transparently invalidated when the Admin Panel
 *     bumps the combined version (`p{privacy}-t{terms}` scheme).
 *   • On accept success, calls `onAccepted(policy.version)` so App.js
 *     persists a version-scoped acceptance memory.
 */
import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Shield, FileText, ExternalLink, CheckSquare, Square, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'policy_content_cache';

// Tiny helper to (a) read from LS cache safely, (b) write on success.
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version && parsed.privacy_policy && parsed.terms_and_conditions) {
      return parsed;
    }
  } catch {
    // fall through
  }
  return null;
};

const writeCache = (policy) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      version: policy.version,
      privacy_policy: policy.privacy_policy || '',
      terms_and_conditions: policy.terms_and_conditions || '',
      published_at: policy.published_at || null,
      content_format: policy.content_format || 'html',
      cached_at: new Date().toISOString(),
    }));
  } catch {
    // Storage quota / private mode — ignore.
  }
};

// Sanitise HTML from the Admin Panel before injecting it into the DOM.
// Keeps a permissive tag list for rich-text formatting (headings, lists,
// tables, links) but strips scripts, event handlers, iframes, etc.
const sanitise = (html) => DOMPurify.sanitize(html || '', {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target', 'rel'],
});

const PolicyAcceptanceModal = ({ onAccepted }) => {
  const { user } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null); // 'privacy' | 'terms' | null
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/legal/policy`);
        const data = await res.json();
        if (!cancelled && data.STATUS === 1 && data.RESULT) {
          setPolicy(data.RESULT);
          writeCache(data.RESULT);
          setFromCache(false);
        }
      } catch {
        // Offline fallback: read the LS cache so the user can at least
        // READ the policy while offline (they still cannot accept until
        // the network returns).
        const cached = readCache();
        if (!cancelled && cached) {
          setPolicy(cached);
          setFromCache(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAccept = useCallback(async () => {
    if (!checked || !policy) return;
    setAccepting(true);
    setError('');
    try {
      const token = require('../services/tokenService').getToken();
      const res = await fetch(`${API_BASE}/api/legal/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          policy_version: policy.version,
          device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
          platform: /Android/.test(navigator.userAgent) ? 'Android'
                  : /iPhone|iPad/.test(navigator.userAgent) ? 'iOS'
                  : navigator.platform?.includes('Win') ? 'Windows'
                  : navigator.platform?.includes('Mac') ? 'Mac' : 'Web',
        }),
      });
      const data = await res.json();
      if (data.STATUS === 1) {
        onAccepted(policy.version);
      } else {
        setError(data.MESSAGE || 'Could not save your acceptance. Please retry.');
      }
    } catch (e) {
      setError('You appear to be offline. Please reconnect and try again.');
    } finally {
      setAccepting(false);
    }
  }, [checked, policy, onAccepted]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[200] bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
      </div>
    );
  }

  // Document viewer
  if (viewingDoc) {
    const content = viewingDoc === 'privacy' ? policy?.privacy_policy : policy?.terms_and_conditions;
    const title = viewingDoc === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions';
    return (
      <div className="fixed inset-0 z-[200] bg-white flex flex-col" data-testid="policy-viewer">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 bg-white sticky top-0">
          <button
            onClick={() => setViewingDoc(null)}
            className="text-[#1B2A7B] font-medium text-sm"
            data-testid="policy-viewer-back"
          >
            Back
          </button>
          <h2 className="font-semibold text-gray-900 flex-1 text-center pr-8">{title}</h2>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-6">
          {content ? (
            <div
              className="policy-html-content text-sm text-gray-700 leading-relaxed"
              data-testid="policy-viewer-content"
              dangerouslySetInnerHTML={{ __html: sanitise(content) }}
            />
          ) : (
            <p className="text-sm text-gray-500">Content not available.</p>
          )}
        </div>
      </div>
    );
  }

  // Main acceptance modal
  return (
    <div className="fixed inset-0 z-[200] bg-[#EEF1FC] flex items-center justify-center p-4" data-testid="policy-modal">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-[#1B2A7B] px-6 pt-8 pb-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Welcome to Superhost</h1>
          <p className="text-white/80 text-sm mt-1">
            {user?.name ? `Hi ${user.name}!` : 'Welcome!'} Please review our policies.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {fromCache && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="policy-cached-notice">
              Showing a cached copy. You can only Accept once you&apos;re back online.
            </p>
          )}

          <p className="text-sm text-gray-600 mb-5">Please review and accept our:</p>

          <div className="space-y-3 mb-6">
            <button
              onClick={() => setViewingDoc('privacy')}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              data-testid="view-privacy-policy"
            >
              <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#1B2A7B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Privacy Policy</p>
                <p className="text-xs text-gray-500">How we handle your data</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            <button
              onClick={() => setViewingDoc('terms')}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              data-testid="view-terms"
            >
              <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#1B2A7B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Terms & Conditions</p>
                <p className="text-xs text-gray-500">Rules for using Superhost</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
          </div>

          <button
            onClick={() => setChecked(!checked)}
            className="flex items-start gap-3 w-full text-left mb-5"
            data-testid="policy-checkbox"
          >
            {checked ? (
              <CheckSquare className="w-5 h-5 text-[#1B2A7B] mt-0.5 flex-shrink-0" />
            ) : (
              <Square className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            )}
            <span className="text-sm text-gray-700 leading-snug">
              I have read and agree to the <span className="font-medium">Privacy Policy</span> and <span className="font-medium">Terms & Conditions</span>.
            </span>
          </button>

          {error && (
            <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3" data-testid="policy-accept-error">
              {error}
            </p>
          )}

          <button
            onClick={handleAccept}
            disabled={!checked || accepting}
            className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all ${
              checked && !accepting
                ? 'bg-[#1B2A7B] text-white hover:bg-[#152163] active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="accept-all-btn"
          >
            {accepting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Accepting...
              </span>
            ) : (
              'Accept All'
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-3">
            Version {policy?.version || 'v1'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PolicyAcceptanceModal;
